// ** React Imports
import { useState, createContext, useEffect } from 'react';

// ** Intl Provider Import
import { IntlProvider } from 'react-intl';

// ** Core Language Data
import messagesEn from '@assets/data/locales/en.json';
import messagesMx from '@assets/data/locales/mx.json';
import messagesDe from '@assets/data/locales/de.json';
import messagesFr from '@assets/data/locales/fr.json';
import messagesPt from '@assets/data/locales/pt.json';

// ** User Language Data
import userMessagesEn from '@src/assets/data/locales/en.json';
import userMessagesMx from '@src/assets/data/locales/mx.json';
import userMessagesDe from '@src/assets/data/locales/de.json';
import userMessagesFr from '@src/assets/data/locales/fr.json';
import userMessagesPt from '@src/assets/data/locales/pt.json';

/* ******* SPANISH JSONs ******* */

import dashboardMx from '@assets/data/locales/mx/dashboard.json';

// ** Per-module translation catalogs (organized by module, see src/assets/data/locales/modules)
import { mergeModuleMessages } from '@src/assets/data/locales/modules';

// ** Menu msg obj
// Module catalogs are spread FIRST so they only fill in keys that don't
// already exist globally — a module must never override an existing
// app-wide translation (e.g. generic words like "Edit"/"Add"/"VAT").
const menuMessages = {
  en: { ...mergeModuleMessages('en'), ...messagesEn, ...userMessagesEn },
  mx: { ...mergeModuleMessages('mx'), ...messagesMx, ...userMessagesMx, ...dashboardMx },
  de: { ...messagesDe, ...userMessagesDe },
  fr: { ...messagesFr, ...userMessagesFr },
  pt: { ...messagesPt, ...userMessagesPt }
};

// ** Create Context
const Context = createContext();

// react-intl needs a real BCP-47 tag to resolve Intl.PluralRules /
// Intl.NumberFormat data (used by ICU `{count, plural, ...}` messages).
// The app's internal locale codes ('mx' for Mexican Spanish) predate that
// requirement, so map them to a valid tag only for the IntlProvider prop —
// every other place in the app keeps using the original code ('mx').
const INTL_LOCALE_MAP = { mx: 'es-MX' };
const toIntlLocale = (lang) => INTL_LOCALE_MAP[lang] || lang;

const IntlProviderWrapper = ({ children }) => {
  // ** States
  const [locale, setLocale] = useState('en');
  const [messages, setMessages] = useState(menuMessages['en']);

  useEffect(() => {
    const langStorage = localStorage.getItem('lang');
    if (langStorage) {
      setLocale(langStorage);
      setMessages(menuMessages[langStorage]);
    }
  }, []);

  // ** Switches Language
  const switchLanguage = (lang) => {
    localStorage.setItem('lang', lang);
    setLocale(lang);
    setMessages(menuMessages[lang]);
  };

  return (
    <Context.Provider value={{ locale, switchLanguage }}>
      <IntlProvider
        key={locale}
        locale={toIntlLocale(locale)}
        messages={messages}
        defaultLocale="en"
      >
        {children}
      </IntlProvider>
    </Context.Provider>
  );
};

export { IntlProviderWrapper, Context as IntlContext };
